/**
 * LeadCallDialog.tsx — THE WARM-LEAD CALLING LANE (2026-09-08, CTO).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * The CTO: *"Occasionally we should provide them the ability to call the warm
 * leads. If it is convertible they can use that lane to call and see whether
 * these patients are happy to pick up so they can do the consultation, and then
 * send the payment links so it nicely aligns with the flow. **It won't be a
 * primary workflow.**"*
 *
 * ── SECONDARY BY CONSTRUCTION ──────────────────────────────────────────────
 * There is no board column, no toolbar chip, no queue and no badge. The one way
 * in is a row in the deal drawer's ⋯ menu, beside the other housekeeping
 * actions — a place a desk goes deliberately, on one card, when they have
 * decided to ring that person. That is the literal reading of "it won't be a
 * primary workflow", and it is also what keeps this lane from competing with
 * the callbacks queue, which is the one on this board currently earning.
 *
 * ── ONE DIALOG, TWO PHASES ─────────────────────────────────────────────────
 * `log` records the call. `interested` then swaps the body for `sent` — the
 * EXISTING PlanLinkActions control, mounted right there, so the operator sends
 * the link while the patient is still on the phone. It is deliberately NOT a
 * second dialog and NOT a jump back to the Overview tab: the whole point of the
 * CTO's flow is that the call and the link are one motion.
 *
 * ⚠️ THE LINK IS SENT BY THE EXISTING CONTROL, NOT BY THIS ONE. PlanLinkActions
 * already owns copying the `#resume=` URL for WhatsApp/SMS and re-sending the
 * plan email, already writes its own audit rows for both, already carries the
 * confirm copy and the server's 429 cap message. Re-implementing any of that
 * here would give the estate two ways to email a patient their plan with two
 * sets of wording and two audit stories.
 *
 * ⚠️ THIS DIALOG NEVER MOVES A CARD INTO THE CLINICIAN QUEUE, and it offers no
 * control that could. Calling is not approving. A lead reaches clinical review
 * by authorising a card, or through the deliberate reason-required manual pull
 * on the board — both of which are elsewhere, on purpose.
 *
 * R41: every rule mirrored below (which dispositions an outcome allows, when a
 * reason is required) is enforced by clinic-service and returns a 400 or a 409
 * regardless of what this form does. It is mirrored for AFFORDANCE — so a desk
 * is never offered a combination that will bounce — never as the control.
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { logLeadCall } from '../api/deals';
import type { CallAttemptOutcome, DealCard, LeadCallDisposition, LeadCallResult } from '../api/types';
import { CALL_OUTCOME_OPTIONS } from '../admin/callbacks/shared';
import { DialogShell } from '../ui/ConfirmDialog';
import { Alert, Button, Field, Help, Select, TextArea } from '../ui/kit';
import { PlanLinkActions } from './PlanLinkActions';

/**
 * The outcomes that mean WE DID NOT GET HOLD OF THEM — the client mirror of
 * clinic-service's `OUTCOMES_THAT_DO_NOT_ADVANCE_PARKING`, read the other way
 * round. `call_back_later` is CONTACT: the patient picked up and asked us to
 * try again, which is an engaged person, not a miss.
 *
 * ⚠️ Written as the NON-CONTACT list rather than the contact one so it matches
 * the server's own array shape. A reviewer comparing the two files is comparing
 * like with like, which is the only way this mirror stays honest.
 */
const NO_CONTACT_OUTCOMES: readonly CallAttemptOutcome[] = [
  'no_answer',
  'voicemail',
  'busy',
  'disconnected',
];

/** Did this outcome reach a human? Mirrors clinic-service `!advancesParking`. */
function reachedThem(outcome: CallAttemptOutcome): boolean {
  return !NO_CONTACT_OUTCOMES.includes(outcome);
}

/**
 * What the person said. Only offered once the outcome says somebody answered —
 * you cannot learn that a patient is not interested from a voicemail, and a
 * lead closed on the strength of one is a patient written off for not picking
 * up.
 */
const DISPOSITION_OPTIONS: Array<{ value: LeadCallDisposition; label: string }> = [
  { value: 'interested', label: 'Interested — send them their plan link' },
  { value: 'not_interested', label: 'Not interested — close this lead' },
];

export function LeadCallDialog({
  deal,
  planLinkLastSentAt,
  planViewedAt,
  onClose,
  onLogged,
}: {
  deal: DealCard;
  /** `dealDetail.assessment.planLinkLastSentAt` — passed straight through. */
  planLinkLastSentAt?: string | null;
  /** `dealDetail.deal.planViewedAt` — passed straight through (T-1). */
  planViewedAt?: string | null;
  onClose: () => void;
  /** Refetch the drawer + the board: an attempt (and possibly a close) landed. */
  onLogged: () => void;
}) {
  const [outcome, setOutcome] = useState<CallAttemptOutcome>('answered');
  const [disposition, setDisposition] = useState<LeadCallDisposition>('interested');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Non-null once the call is recorded — the dialog's second phase. */
  const [result, setResult] = useState<LeadCallResult | null>(null);

  const reached = reachedThem(outcome);
  // The disposition the SERVER will accept for this outcome. A non-contact call
  // has exactly one honest answer, so the picker is not shown at all rather
  // than shown-and-disabled: there is no choice to make.
  const effectiveDisposition: LeadCallDisposition = reached ? disposition : 'no_answer';
  const needsReason = effectiveDisposition === 'not_interested';
  const canSubmit = !busy && (!needsReason || reason.trim().length > 0);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await logLeadCall(deal.dealId, {
        outcome,
        disposition: effectiveDisposition,
        // Omitted rather than sent empty: the server's schema is `.strict()`
        // and a blank string is not a note.
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        ...(needsReason ? { reason: reason.trim() } : {}),
      });
      setResult(res);
      // The board and the drawer are refetched IMMEDIATELY, before the operator
      // does anything with the plan link — the attempt count on the card and
      // (on a close) the stage are already true, and a desk that dismisses this
      // dialog without reading it must still see the right card.
      onLogged();
    } catch (err) {
      // TA-06: the form KEEPS its values — resubmitting IS the retry.
      setError(callFailure(err));
    } finally {
      setBusy(false);
    }
  }

  // ── PHASE 2: the call is on the record; do the one thing it implies ────────
  if (result) {
    return (
      <DialogShell title="Call logged" onCancel={onClose} width="min(560px, 100%)">
        <CallOutcomeSummary result={result} />
        {result.nextStep === 'send_plan_link' ? (
          result.assessmentId ? (
            <div style={{ marginTop: 'var(--gps-s-3)' }}>
              {/* THE EXISTING CONTROL, MOUNTED HERE. Copy for WhatsApp/SMS while
                  the patient is on the phone, or re-send the email. Both write
                  their own audit rows; this dialog sends nothing itself. */}
              <PlanLinkActions assessmentId={result.assessmentId} lastSentAt={planLinkLastSentAt} viewedAt={planViewedAt} />
            </div>
          ) : (
            <Alert tone="warning" role="alert" style={{ marginTop: 'var(--gps-s-3)' }}>
              There is no questionnaire on this deal, so there is no plan link to send. Take their details
              and start the form with them instead.
            </Alert>
          )
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--gps-s-4)' }}>
          <Button type="button" variant="ghost" onClick={onClose} style={{ width: 'auto' }}>
            Done
          </Button>
        </div>
      </DialogShell>
    );
  }

  // ── PHASE 1: record the call ───────────────────────────────────────────────
  return (
    <DialogShell title="Log a call to this lead" onCancel={onClose} width="min(560px, 100%)">
      <form onSubmit={submit}>
        <Help style={{ marginTop: 0 }}>
          {/* Says what this action is AND what it is not. Both halves matter: a
              desk reaching for "call" on a pipeline board has every reason to
              expect a callback ticket, and this deliberately is not one. */}
          A sales call, not a clinical one. Nothing here books a callback or puts the patient in the
          clinician&rsquo;s queue &mdash; they reach it by authorising a card.
        </Help>
        <Field label="How did the call go?" htmlFor="lead-call-outcome">
          <Select
            id="lead-call-outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as CallAttemptOutcome)}
          >
            {CALL_OUTCOME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        {reached ? (
          <Field label="What did they say?" htmlFor="lead-call-disposition">
            <Select
              id="lead-call-disposition"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value as LeadCallDisposition)}
            >
              {DISPOSITION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          // No picker, and a plain sentence instead of a disabled control: with
          // nobody on the phone there is no choice to make, and "the lead stays
          // where it is" is the fact the desk needs — it is what tells them the
          // card will still be there to ring again tomorrow.
          <Help>They didn&rsquo;t pick up, so the attempt is recorded and the lead stays where it is.</Help>
        )}
        {needsReason ? (
          <Field
            label="Why are they not interested?"
            htmlFor="lead-call-reason"
            hint="Required — this closes the lead, and the reason goes on their record."
          >
            <TextArea
              id="lead-call-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
            />
          </Field>
        ) : null}
        <Field label="Call note (optional)" htmlFor="lead-call-note">
          <TextArea
            id="lead-call-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
        </Field>
        {error ? (
          <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {error}
          </Alert>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy} style={{ width: 'auto' }}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit} style={{ width: 'auto' }}>
            {busy ? 'Saving…' : 'Log the call'}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

/**
 * What the server recorded, in the words a desk needs next.
 *
 * ⚠️ THE ATTEMPT COUNT IS RENDERED FROM `attemptsRequired`, NEVER FROM A 3 IN
 * THIS FILE. The threshold is clinic-service's `REQUIRED_ROUND_ATTEMPTS` and it
 * is echoed on the response precisely so this surface cannot hold a stale copy
 * of it.
 *
 * ⚠️ AND "three attempts" IS NOT A DEADLINE. The exhausted line says what to
 * consider, not what has happened: nothing parks this lead automatically, on
 * purpose (parking cascades into the unreachable lane and its nurture emails,
 * which is the wrong thing to do to somebody we have never spoken to). The
 * wording has to leave the decision with the person reading it.
 */
function CallOutcomeSummary({ result }: { result: LeadCallResult }) {
  if (result.nextStep === 'closed') {
    return (
      <Alert tone="info" role="status">
        Call logged and the lead is closed. Nothing else on their record changed.
      </Alert>
    );
  }
  if (result.nextStep === 'send_plan_link') {
    return (
      <Alert tone="success" role="status">
        Call logged. The lead hasn&rsquo;t moved &mdash; send them their plan link and they&rsquo;ll pick a
        treatment and pay from it.
      </Alert>
    );
  }
  return (
    <Alert tone={result.roundExhausted ? 'warning' : 'info'} role="status">
      {`Attempt ${result.attempt} of ${result.attemptsRequired} logged. The lead stays where it is.`}
      {result.roundExhausted
        ? ' That is all the attempts we normally make — consider closing it, or leave it and try again later.'
        : ''}
    </Alert>
  );
}

/**
 * Turn a failure into a sentence a desk can act on. The server's messages here
 * are PHI-free by construction (stage words, counts and vocabulary rules —
 * never a name or a number), so surfacing `detail` verbatim is both safe and far
 * more useful than a generic apology: the 409 on an active call round tells the
 * operator exactly which surface to log the call on instead.
 */
function callFailure(err: unknown): string {
  if (err instanceof ApiError && err.detail) return err.detail;
  return 'Sorry — we could not log that call. Try again, and if it keeps failing use Log activity instead.';
}
